import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../context/AuthContext";
import { useLocale } from "../context/LanguageContext";

/**
 * Magic link verification landing page.
 * User arrives here via: /verify?token=<jwt>
 * Validates the token, then redirects to dashboard.
 */
export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithMagicLink, isAuthenticated } = useAuth();
  const { t } = useLocale();
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setError(t("verify.noToken"));
      setVerifying(false);
      return;
    }

    let cancelled = false;

    async function verify() {
      try {
        await loginWithMagicLink(token!);
        if (!cancelled) {
          navigate("/", { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[VerifyPage] Verification failed:", err);
          setError(t("verify.failed"));
          setVerifying(false);
        }
      }
    }

    void verify();

    return () => {
      cancelled = true;
    };
  }, [searchParams, loginWithMagicLink, navigate, t]);

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated && !verifying) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, verifying, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 text-center shadow-sm">
          <div className="mb-4 text-4xl">⚠️</div>
          <h1 className="mb-2 text-xl font-bold text-foreground">
            {t("verify.failedTitle")}
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            {t("verify.backToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <div className="mb-4 h-8 w-8 mx-auto animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">{t("verify.verifying")}</p>
      </div>
    </div>
  );
}
